import React from 'react';
import pt from 'prop-types';
import { MrInputRadio, MrInputRadioGroup } from '@ion/components';
import FeatureGate from 'app/launch-darkly/gates/feature-gate';
import { FEATURE_FLAGS } from 'app/launch-darkly/featureFlags';
import CONSENT_SOURCE_TYPES, {
  DEFAULT_CONSENT_SOURCE_TYPE,
} from 'app/components/analytics-builder/CONSENT_SOURCE_TYPES';
import ConsentSourceCookieFields from './ConsentSourceCookieFields';
import ConsentSourceJsApiFields from './ConsentSourceJsApiFields';
import CmpPresetSelect from './CmpPresetSelect';
import OneTrustConsentCookieName from './OneTrustConsentCookieName';
import CMP_PRESETS from './CMP_PRESETS';
import ConsentOptInValues from './ConsentOptInValues';
import ConsentGlobalPrivacyControl from './ConsentGlobalPrivacyControl';
import MatchIdConsent from './MatchIdConsent';
import ConsentRefreshNotice from './ConsentRefreshNotice';
import {
  CONSENT_SOURCE_TYPE,
  CONSENT_CMP_PRESET,
  CONSENT_SAVED_CALLBACK_SOURCE,
} from 'app/components/analytics-builder/form-keys';

export default function AnalyticsBuilderConsentSource({
  formState,
  setField,
  validateField,
  validateRequired,
  resetError,
}) {
  const fieldProps = { formState, setField, validateField, validateRequired, resetError };
  const sourceType = formState[CONSENT_SOURCE_TYPE]?.value ?? DEFAULT_CONSENT_SOURCE_TYPE;
  const isJsApi = sourceType === CONSENT_SOURCE_TYPES.JS_API;
  const isCmp = sourceType === CONSENT_SOURCE_TYPES.CMP;
  const isCallback = sourceType === CONSENT_SOURCE_TYPES.CALLBACK_JS_API;
  const savedCallback = formState[CONSENT_SAVED_CALLBACK_SOURCE]?.value ?? '';
  const isOneTrust = isCmp && formState[CONSENT_CMP_PRESET]?.value === CMP_PRESETS.ONETRUST;

  return (
    <div>
      <MrInputRadioGroup
        overrideValue={sourceType}
        name={CONSENT_SOURCE_TYPE}
        label="Consent Source"
        helperText="Where your consent management platform exposes the user's consent decisions."
        onChange={setField}
        resetError={resetError}
        required
      >
        <MrInputRadio
          value={CONSENT_SOURCE_TYPES.COOKIE}
          label="Cookie"
          tooltip="The CMP writes consent state to a browser cookie (e.g. OneTrust's OptanonConsent)."
        />
        <MrInputRadio
          value={CONSENT_SOURCE_TYPES.CMP}
          label="Supported CMP"
          tooltip="Use the built-in OneTrust or Transcend integration, including initialization and preference updates."
        />
        <MrInputRadio
          value={CONSENT_SOURCE_TYPES.JS_API}
          label="Custom JavaScript API"
          tooltip="Read a flat consent map from a custom method or a wrapper maintained by your integration team."
        />
        {(isCallback || Boolean(savedCallback)) && (
          <MrInputRadio value={CONSENT_SOURCE_TYPES.CALLBACK_JS_API} label="Saved callback JavaScript API" />
        )}
      </MrInputRadioGroup>
      {isCallback ? (
        <div>
          <p>
            The saved callback API configuration is preserved, including Ketch settings. These source fields are
            read-only here; use YAML to edit them or choose another consent source to replace them.
          </p>
          <textarea aria-label="Saved callback API configuration" readOnly value={savedCallback} />
          <input type="hidden" name={CONSENT_SAVED_CALLBACK_SOURCE} value={savedCallback} />
        </div>
      ) : isCmp ? (
        <CmpPresetSelect {...fieldProps} />
      ) : isJsApi ? (
        <ConsentSourceJsApiFields {...fieldProps} />
      ) : (
        <ConsentSourceCookieFields {...fieldProps} />
      )}
      {isOneTrust && <OneTrustConsentCookieName {...fieldProps} />}
      <ConsentRefreshNotice automatic={isCmp} />
      {isOneTrust && (
        <p>
          OneTrust&apos;s active groups determine consent: active categories are granted and inactive categories are
          denied. The explicit/implied Consent Policy applies only if OneTrust cannot resolve consent. Local groups do
          not guarantee that an authenticated profile lookup has completed.
        </p>
      )}
      {!isOneTrust && (
        <ConsentOptInValues
          {...fieldProps}
          autoUsesAjsDefault={isCmp && formState[CONSENT_CMP_PRESET]?.value === CMP_PRESETS.TRANSCEND}
        />
      )}
      <ConsentGlobalPrivacyControl formState={formState} setField={setField} resetError={resetError} />
      <FeatureGate flagName={FEATURE_FLAGS.configurableConsentMvp}>
        <FeatureGate flagName={FEATURE_FLAGS.matchIdConsent}>
          <MatchIdConsent {...fieldProps} />
        </FeatureGate>
      </FeatureGate>
    </div>
  );
}

AnalyticsBuilderConsentSource.propTypes = {
  formState: pt.object.isRequired,
  setField: pt.func.isRequired,
  validateField: pt.func.isRequired,
  validateRequired: pt.func.isRequired,
  resetError: pt.func.isRequired,
};
